import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import ReuploadApiResponseDialog from '../../../components/ReuploadDocuments/ReuploadApiResponseDialog';
import ReuploadDocumentsPageLayout from '../../../components/ReuploadDocuments/ReuploadDocumentsPageLayout';
import {
    ReuploadDocumentSlot,
    ReuploadDocumentsGrid,
    buildReuploadDocumentRemarks,
    buildReuploadStatusBarFromSlots,
    isDocFlagApproved,
    isDocFlagRejected,
    isReuploadSubmitDisabled,
    preserveReuploadDocumentValue,
    pickReuploadDocumentUrl,
    REUPLOAD_DOCUMENT_URL_KEYS,
    shouldShowReuploadDocumentSlot,
} from '../../../components/ReuploadDocuments/reuploadDocumentUi';
import { cfpBaseUrl } from '../../../network/urls';
import { handleFileUpload } from '../../../services/Functions';
import { portalName } from '../../../utils/PortalName';

const MeterChangeRequestUpdateDocuments = () => {
    const initialValues = {
        iv_applicant_cnic_document_front: null,
        iv_applicant_cnic_document_back: null,
        iv_scanned_copy_of_near_by_electricity_bill: null,
        iv_any_other_document: null,
    };

    const [apiResponseDialog, setApiResponseDialog] = useState({
        open: false,
        type: 'success',
        message: '',
        documentRemarks: [],
    });
    const [submitCompleted, setSubmitCompleted] = useState(false);
    const [applicantCnicDocumentFrontThumbnail, setApplicantCnicDocumentFrontThumbnail] = useState('');
    const [applicantCnicDocumentBackThumbnail, setApplicantCnicDocumentBackThumbnail] = useState('');
    const [electricityBillDocumentThumbnail, setElectricityBillDocumentThumbnail] = useState('');
    const [anyOtherDocumentThumbnail, setAnyOtherDocumentThumbnail] = useState('');
    const [applicantCnicFrontError, setApplicantCnicFrontError] = useState('');
    const [applicantCnicBackError, setApplicantCnicBackError] = useState('');
    const [electricityBillError, setElectricityBillError] = useState('');
    const [anyOtherDocumentError, setAnyOtherDocumentError] = useState('');
    const [viewBackdrop, setViewBackdrop] = useState(false);
    const [error, setError] = useState(false);
    const [cnicFrontReUploaded, setCnicFrontReUploaded] = useState(false);
    const [cnicBackReUploaded, setCnicBackReUploaded] = useState(false);
    const [electricityBillReUploaded, setElectricityBillReUploaded] = useState(false);
    const [anyOtherReUploaded, setAnyOtherReUploaded] = useState(false);

    const { state } = useLocation();
    const { dialogRowData } = state || {};
    const { token } = useSelector((authState) => authState.auth);
    const navigate = useNavigate();
    const reuploadSources = [dialogRowData];

    useEffect(() => {
        setApplicantCnicDocumentFrontThumbnail(pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.cnicFront));
        setApplicantCnicDocumentBackThumbnail(pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.cnicBack));
        setElectricityBillDocumentThumbnail(pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.electricityBill));
        setAnyOtherDocumentThumbnail(pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.anyOther));
    }, [dialogRowData]);

    const isCnicFrontRejected = isDocFlagRejected(dialogRowData?.applicantCNICFrontFlag);
    const isCnicBackRejected = isDocFlagRejected(dialogRowData?.applicantCNICBackFlag);
    const isElectricityBillRejected = isDocFlagRejected(dialogRowData?.electricityBillImageFlag);
    const isAnyOtherRejected = isDocFlagRejected(dialogRowData?.anyOtherDocumentFlag);
    const isCnicFrontApproved = isDocFlagApproved(dialogRowData?.applicantCNICFrontFlag);
    const isCnicBackApproved = isDocFlagApproved(dialogRowData?.applicantCNICBackFlag);
    const isElectricityBillApproved = isDocFlagApproved(dialogRowData?.electricityBillImageFlag);
    const isAnyOtherApproved = isDocFlagApproved(dialogRowData?.anyOtherDocumentFlag);

    const reuploadSlots = useMemo(() => {
        const slots = [
            { isRejected: isCnicFrontRejected, isReUploaded: cnicFrontReUploaded },
            { isRejected: isCnicBackRejected, isReUploaded: cnicBackReUploaded },
            { isRejected: isElectricityBillRejected, isReUploaded: electricityBillReUploaded },
        ];
        if (shouldShowReuploadDocumentSlot({
            documentValue: pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.anyOther),
            flag: dialogRowData?.anyOtherDocumentFlag,
        })) {
            slots.push({ isRejected: isAnyOtherRejected, isReUploaded: anyOtherReUploaded });
        }
        return slots;
    }, [
        isCnicFrontRejected, isCnicBackRejected, isElectricityBillRejected, isAnyOtherRejected,
        cnicFrontReUploaded, cnicBackReUploaded, electricityBillReUploaded, anyOtherReUploaded,
        dialogRowData?.anyOtherDocument, dialogRowData?.anyOtherDocumentFlag,
    ]);

    const reuploadStatusBar = useMemo(
        () => buildReuploadStatusBarFromSlots(reuploadSlots),
        [reuploadSlots],
    );

    const disableSubmit = isReuploadSubmitDisabled(reuploadSlots, submitCompleted);

    const { handleChange, handleSubmit } = useFormik({
        initialValues,
        validationSchema: Yup.object({
            iv_applicant_cnic_document_front: Yup.mixed().required(
                'Please upload CNIC front in jpg, jpeg, png or pdf format.',
            ),
            iv_applicant_cnic_document_back: Yup.mixed().required(
                'Please upload CNIC back in jpg, jpeg, png or pdf format.',
            ),
            iv_scanned_copy_of_near_by_electricity_bill: Yup.mixed().required(
                'Please upload electricity bill in jpg, jpeg, png or pdf format.',
            ),
        }),
        onSubmit: async () => {
            setViewBackdrop(true);
            const myHeaders = new Headers();
            myHeaders.append('Content-Type', 'application/json');
            myHeaders.append('Authorization', token);

            const raw = JSON.stringify({
                applicationId: dialogRowData.applicationId,
                submittedThrough: portalName,
                applicationProgressId: 1,
                objectionStatus: 0,
                applicantCNICBack: preserveReuploadDocumentValue(applicantCnicDocumentBackThumbnail, dialogRowData?.applicantCNICBack),
                applicantCNICFront: preserveReuploadDocumentValue(applicantCnicDocumentFrontThumbnail, dialogRowData?.applicantCNICFront),
                electricityBillImage: preserveReuploadDocumentValue(electricityBillDocumentThumbnail, dialogRowData?.scannedCopyOfNearByElectricityBill),
                anyOtherDocument: preserveReuploadDocumentValue(anyOtherDocumentThumbnail, dialogRowData?.anyOtherDocument),
                applicantCNICFrontFlag: 2,
                applicantCNICBackFlag: 2,
                electricityBillImageFlag: 2,
                anyOtherDocumentFlag: preserveReuploadDocumentValue(anyOtherDocumentThumbnail, dialogRowData?.anyOtherDocument) ? 2 : '',
                remarksDetails: 'Documents Re-uploaded.',
                remarks: 'Documents Re-uploaded.',
            });

            try {
                const response = await fetch(`${cfpBaseUrl}meterChangeOrder/reuploadMCODocuments`, {
                    method: 'POST',
                    headers: myHeaders,
                    body: raw,
                });
                const result = await response.json();

                if (result?.status === 200 || result?.status === '200') {
                    setSubmitCompleted(true);
                    setApiResponseDialog({
                        open: true,
                        type: 'success',
                        message: result.message || 'Documents re-uploaded successfully.',
                        documentRemarks: buildReuploadDocumentRemarks({}, dialogRowData),
                    });
                } else {
                    setApiResponseDialog({
                        open: true,
                        type: 'error',
                        message: result.message || 'Something went wrong.',
                        documentRemarks: [],
                    });
                }
            } catch (submitError) {
                setApiResponseDialog({
                    open: true,
                    type: 'error',
                    message: 'Failed to re-upload documents.',
                    documentRemarks: [],
                });
            } finally {
                setViewBackdrop(false);
            }
        },
    });

    const handleCnicFrontUpload = (event) => {
        const newFileName = `copyOfApplicantCNICFront.${event.target.files[0].name.split('.').pop()}`;
        handleFileUpload(event, handleChange, () => {}, setApplicantCnicFrontError, setApplicantCnicDocumentFrontThumbnail, newFileName);
        setCnicFrontReUploaded(true);
    };

    const handleCnicBackUpload = (event) => {
        const newFileName = `copyOfApplicantCNICBack.${event.target.files[0].name.split('.').pop()}`;
        handleFileUpload(event, handleChange, () => {}, setApplicantCnicBackError, setApplicantCnicDocumentBackThumbnail, newFileName);
        setCnicBackReUploaded(true);
    };

    const handleElectricityBillUpload = (event) => {
        const newFileName = `electricityBill.${event.target.files[0].name.split('.').pop()}`;
        handleFileUpload(event, handleChange, () => {}, setElectricityBillError, setElectricityBillDocumentThumbnail, newFileName);
        setElectricityBillReUploaded(true);
    };

    const handleAnyOtherUpload = (event) => {
        const newFileName = `anyOtherDocument.${event.target.files[0].name.split('.').pop()}`;
        handleFileUpload(event, handleChange, () => {}, setAnyOtherDocumentError, setAnyOtherDocumentThumbnail, newFileName);
        setAnyOtherReUploaded(true);
    };

    const handleApiResponseDialogClose = () => {
        const wasSuccess = apiResponseDialog.type === 'success';
        setApiResponseDialog((prev) => ({ ...prev, open: false }));
        if (wasSuccess) {
            navigate('/meter-change-request-table', { state: { refreshMcoList: true } });
        }
    };

    return (
        <>
            <ReuploadDocumentsPageLayout
                pageTitle="Meter Change Request - Re-Upload Documents"
                statusBar={reuploadStatusBar}
                dialogRowData={dialogRowData}
                onSubmit={handleSubmit}
                onCancel={() => navigate('/meter-change-request-table')}
                submitDisabled={disableSubmit}
                viewBackdrop={viewBackdrop}
                error={error}
                onErrorClose={() => setError('')}
            >
                <ReuploadDocumentsGrid>
                    <ReuploadDocumentSlot
                                    label="CNIC Front"
                                    thumbnail={applicantCnicDocumentFrontThumbnail}
                                    documentSources={reuploadSources}
                                    documentUrlKeys={REUPLOAD_DOCUMENT_URL_KEYS.cnicFront}
                                    isApproved={isCnicFrontApproved}
                                    isRejected={isCnicFrontRejected}
                                    isReUploaded={cnicFrontReUploaded}
                                    inputId="iv_applicant_cnic_document_front"
                                    inputName="iv_applicant_cnic_document_front"
                                    onUpload={handleCnicFrontUpload}
                                    uploadError={applicantCnicFrontError}
                                />
                                <ReuploadDocumentSlot
                                    label="CNIC Back"
                                    thumbnail={applicantCnicDocumentBackThumbnail}
                                    documentSources={reuploadSources}
                                    documentUrlKeys={REUPLOAD_DOCUMENT_URL_KEYS.cnicBack}
                                    isApproved={isCnicBackApproved}
                                    isRejected={isCnicBackRejected}
                                    isReUploaded={cnicBackReUploaded}
                                    inputId="iv_applicant_cnic_document_back"
                                    inputName="iv_applicant_cnic_document_back"
                                    onUpload={handleCnicBackUpload}
                                    uploadError={applicantCnicBackError}
                                />
                    <ReuploadDocumentSlot
                                    label="Electricity Bill"
                                    thumbnail={electricityBillDocumentThumbnail}
                                    documentSources={reuploadSources}
                                    documentUrlKeys={REUPLOAD_DOCUMENT_URL_KEYS.electricityBill}
                                    isApproved={isElectricityBillApproved}
                                    isRejected={isElectricityBillRejected}
                                    isReUploaded={electricityBillReUploaded}
                                    inputId="iv_scanned_copy_of_near_by_electricity_bill"
                                    inputName="iv_scanned_copy_of_near_by_electricity_bill"
                                    onUpload={handleElectricityBillUpload}
                                    uploadError={electricityBillError}
                                />
                    {shouldShowReuploadDocumentSlot({
                        documentValue: pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.anyOther),
                        flag: dialogRowData?.anyOtherDocumentFlag,
                    }) ? (
                            <ReuploadDocumentSlot
                                            label="Any Other Document"
                                            thumbnail={anyOtherDocumentThumbnail}
                                            documentSources={reuploadSources}
                                            documentUrlKeys={REUPLOAD_DOCUMENT_URL_KEYS.anyOther}
                                            isApproved={isAnyOtherApproved}
                                            isRejected={isAnyOtherRejected}
                                            isReUploaded={anyOtherReUploaded}
                                            inputId="iv_any_other_document"
                                            inputName="iv_any_other_document"
                                            onUpload={handleAnyOtherUpload}
                            uploadError={anyOtherDocumentError}
                        />
                    ) : null}
                </ReuploadDocumentsGrid>
            </ReuploadDocumentsPageLayout>

            <ReuploadApiResponseDialog
                open={apiResponseDialog.open}
                type={apiResponseDialog.type}
                onClose={handleApiResponseDialogClose}
                message={apiResponseDialog.message}
                applicationId={dialogRowData?.applicationId}
                documentRemarks={apiResponseDialog.documentRemarks}
                errorTitle="Request Failed"
            />
        </>
    );
};

export default MeterChangeRequestUpdateDocuments;
